import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, MainButton, useToast } from '../components';
import { useCreateList, useDeleteList, useList, useRenameList } from '../api/queries';
import { useSurface } from '../hooks/useSurface';
import { ApiError } from '../api/client';

/**
 * The three name/confirm dialogs. They are routes, not local state, so the
 * spec's back targets fall out for free: `/new` returns to the overview,
 * `/lists/:id/rename` and `/lists/:id/delete` return to the detail.
 */

export function NewListDialog() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isTelegram } = useSurface();
  const createList = useCreateList();
  const [name, setName] = useState('');

  const close = () => navigate('/');
  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createList.mutate(trimmed, {
      onSuccess: (list) => {
        showToast('List created');
        navigate(`/lists/${list.id}`, { replace: true });
      },
    });
  };

  return (
    <>
      <Dialog open onDismiss={close} labelledBy="dialog-newlist">
        <div className="dialog__pad">
          <div className="dialog__title" id="dialog-newlist" style={{ textAlign: 'center' }}>
            New list
          </div>
          <input
            className="dialog__field"
            value={name}
            autoFocus
            placeholder="Weekend trip"
            aria-label="List name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          {createList.isError ? (
            <div className="dialog__error">{errorText(createList.error)}</div>
          ) : null}
          <div className="dialog__actions">
            <button type="button" className="dialog__btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="dialog__btn dialog__btn--primary"
              onClick={submit}
              disabled={createList.isPending || !name.trim()}
            >
              Create
            </button>
          </div>
        </div>
      </Dialog>
      {isTelegram ? (
        <MainButton label="Create" onClick={submit} loading={createList.isPending} />
      ) : null}
    </>
  );
}

export function RenameListDialog({ listId }: { listId: number }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isTelegram } = useSurface();
  const { data: list } = useList(listId);
  const rename = useRenameList(listId);
  const [name, setName] = useState<string | null>(null);
  const value = name ?? list?.name ?? '';

  const close = () => navigate(`/lists/${listId}`);
  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    rename.mutate(trimmed, {
      onSuccess: () => {
        showToast('List renamed');
        close();
      },
    });
  };

  return (
    <>
      <Dialog open onDismiss={close} labelledBy="dialog-rename">
        <div className="dialog__pad">
          <div className="dialog__title" id="dialog-rename" style={{ textAlign: 'center' }}>
            Rename list
          </div>
          <input
            className="dialog__field"
            value={value}
            autoFocus
            aria-label="List name"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          {rename.isError ? <div className="dialog__error">{errorText(rename.error)}</div> : null}
          <div className="dialog__actions">
            <button type="button" className="dialog__btn" onClick={close}>
              Cancel
            </button>
            <button
              type="button"
              className="dialog__btn dialog__btn--primary"
              onClick={submit}
              disabled={rename.isPending || !value.trim()}
            >
              Save
            </button>
          </div>
        </div>
      </Dialog>
      {isTelegram ? <MainButton label="Save" onClick={submit} loading={rename.isPending} /> : null}
    </>
  );
}

export function DeleteListDialog({ listId }: { listId: number }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { data: list } = useList(listId);
  const remove = useDeleteList(listId);

  const close = () => navigate(`/lists/${listId}`);

  return (
    <Dialog open onDismiss={close} labelledBy="dialog-delete">
      <div className="dialog__head">
        <div className="dialog__title dialog__title--destructive" id="dialog-delete">
          Delete “{list?.name ?? ''}”?
        </div>
        <div className="dialog__body">
          This deletes all items and shares. This can't be undone.
        </div>
      </div>
      <div className="dialog__actions dialog__actions--stacked">
        <button
          type="button"
          className="dialog__btn dialog__btn--destructive"
          disabled={remove.isPending}
          onClick={() =>
            remove.mutate(undefined, {
              onSuccess: () => {
                showToast('List deleted');
                navigate('/', { replace: true });
              },
              onError: () => showToast("Couldn't delete that list"),
            })
          }
        >
          Delete list
        </button>
        <button type="button" className="dialog__btn dialog__btn--cancel-lg" onClick={close}>
          Cancel
        </button>
      </div>
    </Dialog>
  );
}

function errorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return 'A list with this name already exists.';
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
}
